import { CSSProperties, RefObject, useLayoutEffect, useRef, useState } from 'react';

interface UseAutoHeightTransitionOptions {
    /**
     * How far (px) beyond the box edge, on all four sides, content is still
     * allowed to paint before being clipped. Needed because box-shadow paints
     * outside the box but the height animation otherwise clips right at its
     * edge. Should cover the largest shadow blur/offset among the animated
     * content.
     */
    shadowBuffer?: number;
}

interface UseAutoHeightTransitionResult<T extends HTMLElement> {
    containerRef: RefObject<HTMLDivElement>;
    contentRef: RefObject<T>;
    containerStyle: CSSProperties;
}

/**
 * Animates a container's height to match its content's natural height whenever the
 * content changes size (e.g. after a data fetch), instead of letting the layout jump
 * instantly and cause CLS. The container height is always pinned to the last measured
 * value; a ResizeObserver on the (unconstrained) content element reports the new
 * natural height, which is then applied as a transitioned inline style.
 *
 * Uses `overflow-y: clip` with additional padding rather than `overflow: hidden`, so
 * box-shadow on the content isn't cut off at the container edge (a plain `hidden`/
 * `auto` axis clips ink overflow like shadows on ALL sides, not just the constrained
 * axis, even when the other axis is `visible`).
 * Source: Claude
 */
export const useAutoHeightTransition = <T extends HTMLElement = HTMLDivElement>({
    shadowBuffer = 0,
}: UseAutoHeightTransitionOptions = {}): UseAutoHeightTransitionResult<T> => {
    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<T>(null);
    const [height, setHeight] = useState<number | 'auto'>('auto');

    useLayoutEffect(() => {
        const content = contentRef.current;
        if (!content) return;

        const measure = () => {
            setHeight(content.offsetHeight);
        };
        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(content);

        return () => observer.disconnect();
    }, []);

    return {
        containerRef,
        contentRef,
        containerStyle: {
            height,
            overflowY: 'clip', // `hidden` would cut off shadows on all sides
            transition: 'height 500ms ease-in',
            boxSizing: 'content-box',
            paddingBottom: `${shadowBuffer}px`, // prevent shadow from being cut off
        },
    };
};
